/**
 * Formulário de uma residência.
 *
 * Papel: preenche, valida e grava uma casa — nome, apelido, cidade e UF —,
 * servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `residencia` para editar ou
 * `null` para criar, e deverá dar a ele uma `key` diferente por casa. Sem a
 * `key`, o React reaproveita a mesma instância ao trocar de registro e os
 * campos continuam com o que a pessoa via antes, porque `defaultValues` só é
 * lido na montagem.
 *
 * A validação é a de `novaResidenciaSchema`, o mesmo schema da Function. O 409
 * de nome já cadastrado chega sem caminho de campo e por isso aparece acima dos
 * botões — é a resposta esperada para a segunda grafia da mesma casa, e precisa
 * ser lida por quem está cadastrando.
 *
 * A lista de "fica dentro de" exclui a própria residência que está sendo
 * editada. O banco aceitaria a linha apontando para si mesma e a rota a recusa
 * com 409, mas oferecer a opção seria oferecer um erro — e quem escolhesse só
 * descobriria ao salvar.
 */
import {
  type NovaResidencia,
  type Residencia,
  UNIDADES_FEDERATIVAS,
  novaResidenciaSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useResidencias, useSalvarResidencia } from '../hooks/usar-cadastros-base';
import { RESIDENCIA_EM_BRANCO, type ResidenciaNoFormulario } from '../types';

interface FormularioResidenciaProps {
  /** A casa a editar, ou `null` para criar uma nova. */
  residencia: Residencia | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<ResidenciaNoFormulario> | undefined> = {
  nome: 'nome',
  apelido: 'apelido',
  cidade: 'cidade',
  uf: 'uf',
  dentroDeId: 'dentroDeId',
};

/** O valor do `<Select>` que significa "não está dentro de nada". */
const NENHUM = 'nenhum';

/**
 * Converte a residência vinda da API no formato que o formulário guarda.
 *
 * @param residencia - A casa a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais dos quatro campos.
 */
function valoresIniciais(residencia: Residencia | null): ResidenciaNoFormulario {
  if (residencia === null) return { ...RESIDENCIA_EM_BRANCO };

  return {
    nome: residencia.nome,
    apelido: residencia.apelido,
    cidade: residencia.cidade,
    uf: residencia.uf,
    dentroDeId: residencia.dentroDe?.id ?? null,
  };
}

/**
 * O formulário de criação e de edição de residência.
 *
 * @param residencia - A casa a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioResidencia({
  residencia,
  onSalvo,
  onCancelar,
}: FormularioResidenciaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const todas = useResidencias();
  const salvar = useSalvarResidencia();

  // Nem a própria linha, para não oferecer o 409 de ficar dentro de si mesma,
  // nem o que já está dentro dela — isso fecharia um ciclo, e qualquer leitura
  // que subisse a hierarquia nunca terminaria.
  const continentesPossiveis = (todas.data ?? []).filter(
    (candidata) =>
      candidata.id !== residencia?.id && candidata.dentroDe?.id !== residencia?.id,
  );

  const form = useForm<ResidenciaNoFormulario, unknown, NovaResidencia>({
    resolver: zodResolver(novaResidenciaSchema),
    defaultValues: valoresIniciais(residencia),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /**
   * Grava a casa e leva o erro da API para o campo que o causou.
   *
   * O que não tem caminho de campo — o 409 de nome repetido, entre outros —
   * vira a mensagem acima dos botões; sem isso a gravação falharia sem que nada
   * aparecesse na tela.
   */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: residencia?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="residencia-nome">Nome</Label>
          <Input
            id="residencia-nome"
            autoComplete="off"
            placeholder="Como a casa é chamada"
            disabled={enviando}
            {...form.register('nome')}
          />
          {errors.nome && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nome.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="residencia-apelido">Apelido</Label>
          <Input
            id="residencia-apelido"
            autoComplete="off"
            placeholder="A forma curta, para coluna estreita e filtro"
            disabled={enviando}
            {...form.register('apelido')}
          />
          {errors.apelido && (
            <p className="text-sm text-destructive" role="alert">
              {errors.apelido.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="residencia-cidade">Cidade</Label>
          <Input
            id="residencia-cidade"
            autoComplete="off"
            disabled={enviando}
            {...form.register('cidade')}
          />
          {errors.cidade && (
            <p className="text-sm text-destructive" role="alert">
              {errors.cidade.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="residencia-uf">UF</Label>
          <Controller
            control={form.control}
            name="uf"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="residencia-uf" className="w-full">
                  <SelectValue placeholder="Escolha" />
                </SelectTrigger>
                <SelectContent>
                  {UNIDADES_FEDERATIVAS.map((sigla) => (
                    <SelectItem key={sigla} value={sigla}>
                      {sigla}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.uf && (
            <p className="text-sm text-destructive" role="alert">
              {errors.uf.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="residencia-dentro-de">Fica dentro de</Label>
          <Controller
            control={form.control}
            name="dentroDeId"
            render={({ field }) => (
              <Select
                value={field.value ?? NENHUM}
                onValueChange={(valor) => {
                  field.onChange(valor === NENHUM ? null : valor);
                }}
                disabled={enviando || todas.isPending}
              >
                <SelectTrigger id="residencia-dentro-de" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NENHUM}>Não fica dentro de nada</SelectItem>
                  {continentesPossiveis.map((candidata) => (
                    <SelectItem key={candidata.id} value={candidata.id}>
                      {candidata.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-muted-foreground">
            A casa que fica num condomínio aponta para ele aqui. É o que permite cadastrar
            depois outros lugares no mesmo terreno.
          </p>
          {errors.dentroDeId && (
            <p className="text-sm text-destructive" role="alert">
              {errors.dentroDeId.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar residência'}
        </Button>
      </DialogFooter>
    </form>
  );
}
